import React, { useEffect, useMemo, useState } from "react";
import { api, fmt, formatError } from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import SupportWidget from "../components/SupportWidget";
import PartnersMap from "../components/PartnersMap";
import {
  ArrowRight,
  Mail,
  Calendar,
  GraduationCap,
  Building2,
  Ticket,
  Wallet,
  ScanLine,
  UserPlus,
  Sparkles,
  KeyRound,
  Smartphone,
  ChevronDown,
  MapPin,
  Loader2,
  Share2,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
  Gift,
  ShoppingBag,
  Heart,
  MoreHorizontal,
  ShoppingCart,
} from "lucide-react";
import BrandMark from "../components/BrandMark";
import WhatsAppChannelCard from "../components/WhatsAppChannelCard";
import AdsRotator from "../components/AdsRotator";
import RotatingMarketingHeadline from "../components/RotatingMarketingHeadline";
import GiftCardVisual from "../components/GiftCardVisual";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const CARD_IMG =
  "https://customer-assets.emergentagent.com/job_digital-identity-120/artifacts/ongisr65_C1980C3E-4E21-4BC8-9D47-F729E86B8BCD.PNG";

// "Made in Ghana" lifestyle hero — a Ghanaian woman in kente holding her
// physical iCaard. Used in the "Join the family" call-to-action so the
// landing page shows a real human face alongside the abstract card.
const LIFESTYLE_HERO_IMG =
  "https://customer-assets.emergentagent.com/job_digital-identity-120/artifacts/qzd6129j_EE2E3374-2467-4251-BDF4-3611BB6FEB4E.PNG";

const EVENT_DATE_FILTERS = [
  { id: "all", label: "All" },
  { id: "this-week", label: "This Week" },
  { id: "next-week", label: "Next Week" },
  { id: "next-month", label: "Next Month" },
  { id: "beyond-next-month", label: "Beyond Next Month" },
];

function matchesEventDateFilter(event, filter) {
  if (filter === "all") return true;
  if (!event.event_datetime) return false;
  const eventDate = new Date(event.event_datetime);
  if (Number.isNaN(eventDate.getTime())) return false;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const mondayOffset = (today.getDay() + 6) % 7;
  const nextWeekStart = new Date(today);
  nextWeekStart.setDate(today.getDate() - mondayOffset + 7);
  const nextWeekEnd = new Date(nextWeekStart);
  nextWeekEnd.setDate(nextWeekEnd.getDate() + 7);
  const nextMonthStart = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const beyondNextMonthStart = new Date(today.getFullYear(), today.getMonth() + 2, 1);

  if (filter === "this-week") return eventDate >= now && eventDate < nextWeekStart;
  if (filter === "next-week") return eventDate >= nextWeekStart && eventDate < nextWeekEnd;
  if (filter === "next-month") return eventDate >= nextMonthStart && eventDate < beyondNextMonthStart;
  if (filter === "beyond-next-month") return eventDate >= beyondNextMonthStart;
  return true;
}

/** Steps explaining how iCaard works. */
const STEPS = [
  { n: 1, Icon: UserPlus, title: "Create your account", body: "Choose Customer, then register with your name, phone number, password and profile photo." },
  { n: 2, Icon: Wallet, title: "Explore your services", body: "Find food, transport, events, hostels, delivery, printing and professional services." },
  { n: 3, Icon: ScanLine, title: "Book and keep track", body: "Keep tickets, travel passes and gift cards in My Tickets. Track orders, queue positions and bookings." },
  { n: 4, Icon: Sparkles, title: "Manage your account", body: "Review your activity, manage your profile and get help from support agents." },
];

/**
 * Portal-selector — the public landing screen for first-time visitors who
 * haven't picked a portal yet.  After they pick one, PortalContext remembers
 * the choice forever (until logout-and-switch).
 */
export default function PortalSelect() {
  const [images, setImages] = useState({});
  const [events, setEvents] = useState([]);
  const [giftPackages, setGiftPackages] = useState([]);
  const [giftServiceFeePct, setGiftServiceFeePct] = useState(0);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedTier, setSelectedTier] = useState("");
  const [guestAgeConfirmed, setGuestAgeConfirmed] = useState(false);
  const [selectedGiftPackage, setSelectedGiftPackage] = useState(null);
  const [giftRecipient, setGiftRecipient] = useState("");
  const [giftWish, setGiftWish] = useState("");
  const [giftOccasion, setGiftOccasion] = useState("general");
  const [giftCheckoutBusy, setGiftCheckoutBusy] = useState(false);
  const [giftCheckoutError, setGiftCheckoutError] = useState("");
  const [guestGiftSuccess, setGuestGiftSuccess] = useState(null);
  const [guestGiftVerifying, setGuestGiftVerifying] = useState(false);
  const [giftReturnActive, setGiftReturnActive] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [trendingIndex, setTrendingIndex] = useState(0);
  const [trendingPaused, setTrendingPaused] = useState(false);
  const [giftSlideIndex, setGiftSlideIndex] = useState(0);
  const [giftSliderPaused, setGiftSliderPaused] = useState(false);
  const [eventDateFilter, setEventDateFilter] = useState("all");
  useEffect(() => { api.get("/portal/images").then(r => setImages(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    let alive = true;
    api.get("/gift/public-packages").then(r => {
      if (alive) {
        setGiftPackages(Array.isArray(r.data?.rows) ? r.data.rows : []);
        setGiftServiceFeePct(Number(r.data?.service_fee_pct || 0));
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("gift_checkout") !== "1") return;
    setGiftReturnActive(true);
    let checkout;
    try { checkout = JSON.parse(localStorage.getItem("icaard_guest_gift_checkout") || "null"); } catch {}
    const reference = params.get("reference") || params.get("trxref") || checkout?.reference;
    if (!reference || !checkout?.claim_token) { setGiftCheckoutError("We could not find this gift checkout on this device. If payment was taken, keep the Paystack receipt and contact iCaard support."); return; }
    setGuestGiftVerifying(true);
    api.post("/gift/guest-checkout/verify", { reference, claim_token: checkout.claim_token })
      .then(r => {
        if (r.data.status === "paid" && r.data.gift) {
          setGuestGiftSuccess({ ...r.data.gift, amount: r.data.amount });
          localStorage.removeItem("icaard_guest_gift_checkout");
          window.history.replaceState({}, "", `${window.location.pathname}${window.location.hash}`);
        } else if (r.data.status === "failed") setGiftCheckoutError("Paystack did not complete this payment. No gift card has been issued.");
        else setGiftCheckoutError("We are still waiting for Paystack to confirm your payment. You can check again below.");
      })
      .catch(e => setGiftCheckoutError(formatError(e)))
      .finally(() => setGuestGiftVerifying(false));
  }, []);
  useEffect(() => {
    let alive = true;
    api.get("/events").then(r => { if (alive) setEvents(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {}).finally(() => { if (alive) setEventsLoading(false); });
    return () => { alive = false; };
  }, []);
  const trendingEvents = useMemo(() => [...events]
    .sort((a, b) => Number(b.views_count || 0) - Number(a.views_count || 0))
    .slice(0, 5), [events]);
  const filteredEvents = useMemo(() => events
    .filter(event => matchesEventDateFilter(event, eventDateFilter))
    .sort((a, b) => {
      const dateA = a.event_datetime ? new Date(a.event_datetime).getTime() : Number.MAX_SAFE_INTEGER;
      const dateB = b.event_datetime ? new Date(b.event_datetime).getTime() : Number.MAX_SAFE_INTEGER;
      return dateA - dateB;
    }), [events, eventDateFilter]);
  useEffect(() => {
    if (trendingIndex >= trendingEvents.length) setTrendingIndex(0);
  }, [trendingIndex, trendingEvents.length]);
  useEffect(() => {
    if (trendingPaused || trendingEvents.length < 2 || window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return undefined;
    const timer = window.setInterval(() => setTrendingIndex(index => (index + 1) % trendingEvents.length), 5200);
    return () => window.clearInterval(timer);
  }, [trendingPaused, trendingEvents.length]);
  useEffect(() => {
    if (giftSlideIndex >= giftPackages.length) setGiftSlideIndex(0);
  }, [giftSlideIndex, giftPackages.length]);
  useEffect(() => {
    if (giftSliderPaused || giftPackages.length < 2 || window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return undefined;
    const timer = window.setInterval(() => setGiftSlideIndex(index => (index + 1) % giftPackages.length), 5600);
    return () => window.clearInterval(timer);
  }, [giftSliderPaused, giftPackages.length]);
  const activeGiftPackage = giftPackages[giftSlideIndex] || giftPackages[0] || null;
  const nav = useNavigate();
  const { user } = useAuth();
  useEffect(() => {
    if (user) nav("/app", { replace: true });
  }, [user, nav]);

  const signInOptions = [
    { title: "Business & Provider Login", detail: "Providers, merchants and company accounts · phone + PIN", icon: KeyRound, href: "/auth/pin" },
    { title: "PIN-only Staff Login", detail: "Staff and team members with an assigned PIN", icon: Smartphone, href: "/admin/icaard" },
  ];
  const providerOptions = signInOptions;

  const shareEvent = async (event) => {
    const url = `${window.location.origin}/api/share/event/${encodeURIComponent(event.id)}`;
    const data = { title: event.name, text: `Join me at ${event.name}`, url };
    if (navigator.share) {
      try { await navigator.share(data); return; }
      catch (error) { if (error?.name === "AbortError") return; }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Event link copied. Share it with your friends.");
    } catch {
      window.prompt("Copy this event link to share it", url);
    }
  };

  const beginTicketCheckout = async (event) => {
    const tiers = event.ticket_tiers || [];
    if (event.student_benefits_enabled && event.student_benefits_type === "students_only") {
      nav(`/auth?next=${encodeURIComponent(`/app/events/${event.id}`)}`);
      return;
    }
    if (event.age_restriction > 0 && !guestAgeConfirmed) {
      setCheckoutError(`Please confirm that you meet the ${event.age_restriction}+ age requirement.`);
      return;
    }
    if (Number(event.ticket_price || 0) <= 0 && !tiers.some(t => Number(t.price) > 0)) {
      nav("/join");
      return;
    }
    const available = tiers.filter(t => t.availability !== "sold_out");
    const tier = tiers.length ? available.find(t => t.id === selectedTier) || available[0] : null;
    if (tiers.length && !tier) {
      setCheckoutError("This event is sold out.");
      return;
    }
    if (!user) {
      localStorage.setItem("icard_ticket_signup_resume", JSON.stringify({
        event_id: event.id, event_name: event.name, tier_id: tier?.id || "",
        tier_name: tier?.name || "General admission", amount: Number(tier?.price ?? event.ticket_price ?? 0),
        age_confirmed: event.age_restriction > 0 ? guestAgeConfirmed : false,
      }));
      nav("/join?ticket_signup=1");
      return;
    }
    setCheckoutBusy(true); setCheckoutError("");
    try {
      const { data } = await api.post("/events/guest-checkout/init", {
        event_id: event.id, tier_id: tier?.id || "", email: "",
        age_confirmed: event.age_restriction > 0 ? guestAgeConfirmed : false,
        callback_url: `${window.location.origin}/join?event_checkout=1`,
      });
      localStorage.setItem("icard_guest_event_checkout", JSON.stringify({
        reference: data.reference, claim_token: data.claim_token,
        event_name: data.event_name || event.name, amount: data.amount,
      }));
      window.location.assign(data.authorization_url);
    } catch (e) { setCheckoutError(formatError(e)); setCheckoutBusy(false); }
  };

  const beginGiftCheckout = async () => {
    if (!selectedGiftPackage || !giftRecipient.trim()) return;
    setGiftCheckoutBusy(true); setGiftCheckoutError("");
    try {
      const { data } = await api.post("/gift/guest-checkout/init", {
        package_id: selectedGiftPackage.id,
        recipient_name: giftRecipient.trim(),
        wish: giftWish.trim(),
        occasion: giftOccasion,
        callback_url: `${window.location.origin}/login?gift_checkout=1`,
      });
      localStorage.setItem("icaard_guest_gift_checkout", JSON.stringify({ reference: data.reference, claim_token: data.claim_token }));
      window.location.assign(data.authorization_url);
    } catch (e) { setGiftCheckoutError(formatError(e)); setGiftCheckoutBusy(false); }
  };

  const verifyGuestGiftCheckout = async () => {
    let checkout;
    try { checkout = JSON.parse(localStorage.getItem("icaard_guest_gift_checkout") || "null"); } catch {}
    if (!checkout?.reference || !checkout?.claim_token) return;
    setGuestGiftVerifying(true); setGiftCheckoutError("");
    try {
      const { data } = await api.post("/gift/guest-checkout/verify", checkout);
      if (data.status === "paid" && data.gift) {
        setGuestGiftSuccess({ ...data.gift, amount: data.amount });
        localStorage.removeItem("icaard_guest_gift_checkout");
        window.history.replaceState({}, "", `${window.location.pathname}${window.location.hash}`);
      } else if (data.status === "failed") setGiftCheckoutError("Paystack did not complete this payment. No gift card has been issued.");
      else setGiftCheckoutError("Payment is not confirmed yet. Please wait a moment and check again.");
    } catch (e) { setGiftCheckoutError(formatError(e)); }
    finally { setGuestGiftVerifying(false); }
  };

  const shareGuestGift = async () => {
    if (!guestGiftSuccess?.token) return;
    const url = `${window.location.origin}/gift/${guestGiftSuccess.token}`;
    try {
      if (navigator.share) { await navigator.share({ title: "An iCaard gift for you", text: `A gift from ${guestGiftSuccess.buyer_name || "someone special"}`, url }); return; }
      await navigator.clipboard.writeText(url);
      toast.success("Gift link copied. Send it to the recipient.");
    } catch (e) {
      if (e?.name !== "AbortError") window.prompt("Copy this gift link to share", url);
    }
  };

  return (
    <div
      className="icaard-marketplace-page relative min-h-screen overflow-hidden text-[#173B2D] bg-[#F4F7F5]"
      data-testid="portal-select-page"
    >
      {/* Header */}
      <header className="icaard-marketplace-header relative z-10 pt-safe">
        <div className="max-w-6xl mx-auto px-5 h-[72px] flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2">
            <BrandMark size={64} />
          </Link>
          <nav className="flex items-center gap-2 sm:gap-3" aria-label="Account navigation">
            <Link to="/auth" className="icaard-marketplace-nav-link">Sign in</Link>
            <Link to="/join" className="icaard-marketplace-nav-cta">Create account</Link>
            <Link to="/legal" className="icaard-marketplace-legal" data-testid="legal-link">Legal</Link>
          </nav>
        </div>
      </header>

      <main className="icaard-marketplace-main relative z-10 max-w-6xl mx-auto px-5 pb-10">
        <section className="icaard-marketplace-hero" aria-labelledby="marketplace-title">
          <img
            className="icaard-marketplace-hero-bg"
            src={images.hero_image || CARD_IMG}
            alt=""
            aria-hidden="true"
            data-testid="portal-hero-background"
            loading="eager"
          />
          <div className="icaard-marketplace-hero-copy">
            <span className="icaard-marketplace-eyebrow"><Ticket size={14}/> ONE CARD · ONE SYSTEM · UNLIMITED ACCESS</span>
            <h1 id="marketplace-title"><RotatingMarketingHeadline /></h1>
            <p>Find live events across Ghana, book your ticket securely, and keep it ready in your iCaard profile.</p>
            <div className="icaard-marketplace-hero-actions">
              <a href="#upcoming-events" className="icaard-marketplace-primary">Explore events <ArrowRight size={17}/></a>
              <span><span className="icaard-marketplace-status-dot"/> Secure checkout with Paystack</span>
            </div>
          </div>
          <div className="icaard-marketplace-hero-caption"><span>Discover</span><i/><span>Book</span><i/><span>Go</span></div>
        </section>

        <section className="icaard-marketplace-events" id="upcoming-events" data-testid="public-event-tickets">
          {!eventsLoading && trendingEvents.length > 0 && (() => {
            const event = trendingEvents[trendingIndex] || trendingEvents[0];
            const eventDate = event.event_datetime ? new Date(event.event_datetime) : null;
            const tiers = event.ticket_tiers || [];
            const available = tiers.filter(t => t.availability !== "sold_out");
            const prices = available.map(t => Number(t.price));
            const price = tiers.length ? (prices.length ? Math.min(...prices) : Number(event.ticket_price || 0)) : Number(event.ticket_price || 0);
            const soldOut = event.status === "sold_out" || event.availability === "sold_out" || (tiers.length > 0 && !available.length);
            const selectEvent = () => { setSelectedEvent(event); setSelectedTier(available[0]?.id || ""); setGuestAgeConfirmed(false); setCheckoutError(""); };
            return <section
              className="icaard-trending-carousel"
              aria-label="Trending event tickets"
              aria-roledescription="carousel"
              onMouseEnter={() => setTrendingPaused(true)}
              onMouseLeave={() => setTrendingPaused(false)}
              onFocusCapture={() => setTrendingPaused(true)}
              onBlurCapture={e => { if (!e.currentTarget.contains(e.relatedTarget)) setTrendingPaused(false); }}
            >
              <div className="icaard-trending-art">
                {event.media_url && event.media_type !== "video" ? <img src={event.media_url} alt="" loading="lazy" /> : event.media_url && event.media_type === "video" ? <video src={event.media_url} muted playsInline /> : <div className="icaard-trending-no-art"><Ticket size={38}/></div>}
                <span className="icaard-trending-badge"><Sparkles size={13}/> Trending now</span>
                <span className="icaard-trending-count"><Eye size={14}/>{Number(event.views_count || 0).toLocaleString()} views</span>
              </div>
              <div className="icaard-trending-copy" key={event.id}>
                <span className="icaard-marketplace-eyebrow">{event.category || "LIVE EVENT"}</span>
                <h3>{event.name}</h3>
                <p>{event.venue || "Venue to be announced"}{eventDate ? ` · ${eventDate.toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}</p>
                <div className="icaard-trending-bottom">
                  <span className="icaard-marketplace-price-label">TICKETS FROM<strong>{price > 0 ? fmt(price) : "Free"}</strong></span>
                  <button type="button" disabled={soldOut} onClick={selectEvent} className="icaard-marketplace-buy">{soldOut ? "Sold out" : "Get tickets"}<ArrowRight size={15}/></button>
                </div>
                {trendingEvents.length > 1 && <div className="icaard-trending-controls" aria-label="Choose featured event">
                  <button type="button" aria-label="Previous trending event" onClick={() => setTrendingIndex(i => (i - 1 + trendingEvents.length) % trendingEvents.length)}><ChevronLeft size={17}/></button>
                  <div>{trendingEvents.map((item, i) => <button key={item.id} type="button" aria-label={`Show trending event ${i + 1}: ${item.name}`} aria-current={i === trendingIndex ? "true" : undefined} onClick={() => setTrendingIndex(i)} />)}</div>
                  <button type="button" aria-label="Next trending event" onClick={() => setTrendingIndex(i => (i + 1) % trendingEvents.length)}><ChevronRight size={17}/></button>
                </div>}
              </div>
            </section>;
          })()}




          <div className="icaard-ticket-list-sticky-scope">
          <div className="icaard-upcoming-sticky">
          <div className="icaard-marketplace-section-heading icaard-upcoming-heading">
            <div>
              <div className="icaard-marketplace-eyebrow">GOOD TIMES ARE CLOSER THAN YOU THINK</div>
              <h2>Upcoming Events</h2>
              <p>Find your next experience and choose the ticket that suits you.</p>
            </div>
            <Link to="/auth?next=%2Fapp%2Ftickets" className="icaard-marketplace-secondary-link">Already have tickets? Sign in <ArrowRight size={15}/></Link>
          </div>
          <div className="icaard-event-date-filters" role="group" aria-label="Filter upcoming events by date">
            {EVENT_DATE_FILTERS.map(filter => <button key={filter.id} type="button" aria-pressed={eventDateFilter === filter.id} onClick={() => setEventDateFilter(filter.id)}>{filter.label}</button>)}
          </div>
          </div>
          {eventsLoading ? <div className="icaard-marketplace-loading"><span/><span/><span/></div>
            : filteredEvents.length ? <div className="icaard-marketplace-event-grid">
              {filteredEvents.map(event => {
                const tiers = event.ticket_tiers || [];
                const available = tiers.filter(t => t.availability !== "sold_out");
                const unavailable = event.status === "sold_out" || event.availability === "sold_out" || (tiers.length > 0 && !available.length);
                const availablePrices = available.map(t => Number(t.price));
                const price = tiers.length ? (availablePrices.length ? Math.min(...availablePrices) : Number(event.ticket_price || 0)) : Number(event.ticket_price || 0);
                const eventDate = event.event_datetime ? new Date(event.event_datetime) : null;
                return <article key={event.id} className="icaard-marketplace-event-card">
                  <div className="icaard-marketplace-event-art">
                    {event.media_url && event.media_type !== "video" ? <img src={event.media_url} alt={event.name} loading="lazy" /> : event.media_url && event.media_type === "video" ? <video src={event.media_url} muted playsInline /> : <div className="icaard-marketplace-no-art"><Ticket size={42}/><span>iCaard Events</span></div>}
                    <span className="icaard-marketplace-category">{event.category || "EVENT"}</span>
                    {event.age_restriction > 0 && <span className="icaard-marketplace-age">{event.age_restriction}+</span>}
                    {eventDate && <span className="icaard-marketplace-date"><b>{eventDate.toLocaleDateString("en-GB", { day: "2-digit" })}</b><small>{eventDate.toLocaleDateString("en-GB", { month: "short" }).toUpperCase()}</small><small className="icaard-marketplace-year">{eventDate.getFullYear()}</small></span>}
                  </div>
                  <div className="icaard-marketplace-event-copy">
                    <h3>{event.name}</h3>
                    <div className="icaard-marketplace-event-meta">
                      <span><MapPin size={14}/>{event.venue || "Venue to be announced"}</span>
                      <div className="icaard-marketplace-date-share">
                        <span><Calendar size={14}/>{eventDate ? eventDate.toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "Date to be announced"}</span>
                      </div>
                    </div>
                    <div className="icaard-marketplace-event-bottom">
                      <span className="icaard-marketplace-price-label">TICKETS FROM<strong>{price > 0 ? fmt(price) : "Free"}</strong></span>
                      <div className="icaard-marketplace-event-actions">
                        <span className="icaard-marketplace-views" aria-label={`${Number(event.views_count || 0).toLocaleString()} views`} title="Event views"><Eye size={17}/>{Number(event.views_count || 0).toLocaleString()}</span>
                        <button type="button" onClick={() => shareEvent(event)} className="icaard-marketplace-share" aria-label={`Share ${event.name}`} title={`Share ${event.name}`}><Share2 size={17}/></button>
                        <button type="button" disabled={unavailable} onClick={() => { setSelectedEvent(event); setSelectedTier(available[0]?.id || ""); setGuestAgeConfirmed(false); setCheckoutError(""); }} className="icaard-marketplace-buy">{unavailable ? "Sold out" : "Get tickets"}<ArrowRight size={17}/></button>
                      </div>
                    </div>
                  </div>
                </article>;
              })}
            </div>
            : <div className="icaard-marketplace-empty"><span><Ticket size={20}/></span><strong>{eventDateFilter === "all" ? "New events are on the way" : "No events in this date range"}</strong><p>{eventDateFilter === "all" ? "No upcoming events are available right now. Check back soon." : "Try another date filter to see more iCaard events."}</p>{eventDateFilter !== "all" && <button type="button" onClick={() => setEventDateFilter("all")}>Show all events <ArrowRight size={14}/></button>}</div>}
          </div>
          {giftPackages.length > 0 && <section className="icaard-marketplace-gifts" aria-labelledby="public-gifts-title" data-testid="public-gift-cards">
            <div className="icaard-gift-hero">
              <div className="icaard-gift-hero-copy">
                <div className="icaard-marketplace-eyebrow"><Gift size={13}/> A LITTLE SOMETHING FOR SOMEONE</div>
                <h2 id="public-gifts-title">Give an iCaard Gift Card</h2>
                <p>Share useful services and treats with someone you care about.</p>
                <div className="icaard-gift-benefits">
                  <div><span className="icaard-gift-benefit-icon is-shop"><ShoppingBag size={23}/></span><strong>Shop services</strong><small>Use across iCaard services</small></div>
                  <div><span className="icaard-gift-benefit-icon is-care"><Heart size={23}/></span><strong>Show you care</strong><small>Perfect for family and friends</small></div>
                  <div><span className="icaard-gift-benefit-icon is-flex"><MoreHorizontal size={23}/></span><strong>Flexible amounts</strong><small>Choose the value that fits</small></div>
                </div>
                <div className="icaard-gift-hero-actions">
                  <Link to="/auth?next=%2Fapp%2Fgift-cards" className="icaard-marketplace-gifts-link"><Gift size={19}/>Explore gift cards <ArrowRight size={19}/></Link>
                  <button type="button" className="icaard-gift-buy-link" onClick={() => {
                    const pkg = activeGiftPackage;
                    if (!pkg) return;
                    setSelectedGiftPackage(pkg); setGiftRecipient(""); setGiftWish(""); setGiftOccasion("general"); setGiftCheckoutError("");
                  }}><ShoppingCart size={20}/>Buy this gift <ArrowRight size={19}/></button>
                </div>
              </div>
              {activeGiftPackage && <div className="icaard-gift-hero-art icaard-gift-carousel" aria-roledescription="carousel" aria-label="Available iCaard gift cards"
                onMouseEnter={() => setGiftSliderPaused(true)} onMouseLeave={() => setGiftSliderPaused(false)}
                onFocusCapture={() => setGiftSliderPaused(true)} onBlurCapture={e => { if (!e.currentTarget.contains(e.relatedTarget)) setGiftSliderPaused(false); }}>
                <div className="icaard-gift-carousel-visual" key={activeGiftPackage.id}>
                  <GiftCardVisual offer gift={{
                    id: `public-preview-${activeGiftPackage.id}`, design_idx: activeGiftPackage.design_idx || 0, package_name: activeGiftPackage.name, token: "preview",
                    price: activeGiftPackage.price,
                    components: (activeGiftPackage.components || []).map((c, i) => ({ key: String(i), label: c.label, type: c.type, remaining: Number(c.value) || 0, total: Number(c.value) || 0, unit: c.unit })),
                  }} />
                </div>
                <div className="icaard-gift-carousel-copy" key={`gift-copy-${activeGiftPackage.id}`}>
                  <span className="icaard-gift-carousel-kicker"><Gift size={14}/> FEATURED GIFT {giftPackages.length > 1 ? `${giftSlideIndex + 1} OF ${giftPackages.length}` : "CARD"}</span>
                  <h3>{activeGiftPackage.name}</h3>
                  <p>{activeGiftPackage.description || "Send a thoughtful iCaard gift that can be used across participating services."}</p>
                  <div className="icaard-gift-carousel-footer"><strong>{fmt(Number(activeGiftPackage.price))}</strong>
                    <button type="button" className="icaard-gift-carousel-buy" onClick={() => {
                      setSelectedGiftPackage(activeGiftPackage); setGiftRecipient(""); setGiftWish(""); setGiftOccasion("general"); setGiftCheckoutError("");
                    }}>Buy this gift <ArrowRight size={16}/></button>
                  </div>
                  {giftPackages.length > 1 && <div className="icaard-gift-carousel-controls" aria-label="Browse gift cards">
                    <button type="button" aria-label="Previous gift card" onClick={() => setGiftSlideIndex(i => (i - 1 + giftPackages.length) % giftPackages.length)}><ChevronLeft size={17}/></button>
                    <div>{giftPackages.map((pkg, i) => <button key={pkg.id} type="button" aria-label={`Show gift card ${i + 1}: ${pkg.name}`} aria-current={i === giftSlideIndex ? "true" : undefined} onClick={() => setGiftSlideIndex(i)} />)}</div>
                    <button type="button" aria-label="Next gift card" onClick={() => setGiftSlideIndex(i => (i + 1) % giftPackages.length)}><ChevronRight size={17}/></button>
                  </div>}
                </div>
              </div>}
            </div>
          </section>}
        </section>

        <section className="icaard-marketplace-account" id="account-access" aria-labelledby="account-access-title">
          <div className="mt-4" data-testid="login-sponsored-ad"><AdsRotator fitCreative /></div>
          <details className="service-provider-dropdown" data-testid="provider-signin-options">
            <summary data-testid="provider-signin-toggle"><span className="flex min-w-0 items-center gap-3"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#147D60]/10 text-[#147D60]"><Building2 size={17}/></span><span className="min-w-0"><strong>Business &amp; staff sign-in</strong><small>For providers, merchants, companies and teams</small></span></span><ChevronDown size={17}/></summary>
            <div id="provider-portal-grid" className="grid grid-cols-1 gap-2 p-3" data-testid="provider-portal-grid" style={{ gridTemplateColumns: "1fr", margin: 0 }}>
              {providerOptions.map(({ title, detail, icon: Icon, href }) => <button key={href} onClick={() => nav(href)} data-testid={`signin-${href === "/auth/pin" ? "phone-pin" : "pin-only"}`} className="group flex items-center gap-3 rounded-xl border border-[#DDE8E1] bg-white p-3 text-left text-[#173B2D] transition hover:border-[#A9D4B7] hover:bg-[#F8FBF9]"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#147D60]/10 text-[#147D60]"><Icon size={16}/></span><span className="min-w-0 flex-1"><span className="block text-sm font-bold !text-[#173B2D]">{title}</span><span className="mt-0.5 block text-[10px] !text-[#607A6D]">{detail}</span></span><ArrowRight size={15} className="shrink-0 text-[#147D60]"/></button>)}
            </div>
          </details>
        </section>

        <section className="mt-12" data-testid="landing-faq">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#A78BFA]">A few quick answers</div>
          <h2 className="font-display font-black text-2xl sm:text-3xl mt-2 leading-tight">Getting started is simple.</h2>
          <div className="grid md:grid-cols-2 gap-3 mt-5">
            <details className="landing-faq-item">
              <summary>What is iCaard?</summary>
              <p>One place to discover services, manage bookings and keep tickets, passes and gift cards ready.</p>
            </details>
            <details className="landing-faq-item">
              <summary>Do I need a physical card?</summary>
              <p>No, you can start with your digital iCaard right away. Yes, you can also choose a beautiful physical iCaard to carry and use for the same iCaard services. Start digitally, then get or link a physical card whenever you’re ready.</p>
            </details>
            <details className="landing-faq-item">
              <summary>Which portal should I choose?</summary>
              <p>Choose Customer for personal services. If you provide a service or run a business, choose the portal that matches your role.</p>
            </details>
            <details className="landing-faq-item">
              <summary>Need help signing in?</summary>
              <p>Open generic login and use the support link, or email <a href="mailto:info@icaard.com">info@icaard.com</a>.</p>
            </details>
          </div>
        </section>

        <div className="mt-5"><SupportWidget publicWelcome /></div>

        {/* HOW IT WORKS */}
        <section className="mt-20" data-testid="how-it-works">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#A78BFA]">
            How iCaard works
          </div>
          <h2 className="font-display font-black text-2xl sm:text-4xl mt-2 leading-tight">
            Your everyday services, connected.
          </h2>
          <p className="text-sm text-[#9CA3D4] mt-2 max-w-xl">
            One place to discover services, manage bookings and keep your passes ready.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {STEPS.map(({ n, Icon, title, body }) => (
              <div
                key={n}
                data-testid={`step-${n}`}
                className="relative bg-[#13131E]/70 border border-[#26263A] rounded-2xl p-5 backdrop-blur-sm"
              >
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-[#7C3AED] flex items-center justify-center font-black text-sm shadow-lg">
                  {n}
                </div>
                <div className="w-10 h-10 rounded-xl bg-[#1A1A27] border border-[#26263A] flex items-center justify-center text-[#A78BFA] mb-3">
                  <Icon size={18} />
                </div>
                <div className="font-display font-bold text-base">{title}</div>
                <p className="text-[12px] text-[#9CA3D4] mt-1 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* PARTNERS & SHOPS DIRECTORY (Round 53g) */}
        <section className="mt-20" data-testid="partners-directory-section">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#A78BFA]">
            Partners & Shops
          </div>
          <h2 className="font-display font-black text-2xl sm:text-4xl mt-2 leading-tight">
            The iCaard family, in your neighbourhood.
          </h2>
          <p className="text-sm text-[#9CA3D4] mt-2 max-w-xl">
            Discover food vendors, hostels, service providers, print shops and merchants already on iCaard.
            Explore local businesses and service providers listed on iCaard.
          </p>
          <div className="mt-8">
            <PartnersMap limit={200} height={480} />
          </div>
        </section>

        {/* JOIN THE FAMILY */}
        <section
          className="mt-20 relative overflow-hidden rounded-3xl border border-[#26263A] bg-gradient-to-br from-[#13131E] via-[#1A1A27] to-[#09090F] p-8 sm:p-12"
          data-testid="join-family"
        >
          <div className="absolute -top-10 -right-10 w-60 h-60 rounded-full bg-[#7C3AED]/20 blur-3xl" aria-hidden="true" />
          <div className="absolute -bottom-10 -left-10 w-60 h-60 rounded-full bg-[#F59E0B]/15 blur-3xl" aria-hidden="true" />

          <div className="relative grid lg:grid-cols-[1fr_1.1fr] gap-10 items-center">
            {/* Lifestyle photo — real Ghanaian iCaard holder */}
            <div className="relative" data-testid="lifestyle-hero">
              <div className="absolute -inset-3 rounded-[2rem] bg-[#FCD34D]/10 blur-2xl" aria-hidden="true" />
              <img
                src={images.lifestyle_image || LIFESTYLE_HERO_IMG}
                alt="A proud Ghanaian iCaard holder. Made in Ghana"
                loading="lazy"
                className="relative w-full max-w-md mx-auto rounded-3xl shadow-2xl shadow-[#7C3AED]/30 ring-1 ring-[#26263A]"
              />
              <div className="text-center mt-3 text-[11px] tracking-[0.25em] uppercase text-[#FCD34D]">
                Made in Ghana · For Ghana
              </div>
            </div>

            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#FCD34D]">
                Join the iCaard family
              </div>
              <h2 className="font-display font-black text-2xl sm:text-4xl mt-2 leading-tight">
                One account for your everyday services.
              </h2>
              <p className="text-sm text-[#9CA3D4] mt-3 max-w-xl">
                Whether you&apos;re a student, a market trader, a hostel owner, an event organiser, a transport
                operator or a print shop owner, there&apos;s a portal built for you.
              </p>

              <div className="flex flex-wrap gap-3 mt-6">
                <a
                  href="mailto:info@icaard.com"
                  data-testid="join-cta-email"
                  className="inline-flex items-center gap-2 border border-[#26263A] hover:border-[#7C3AED] text-white font-bold text-sm px-5 py-3 rounded-xl transition"
                >
                  <Mail size={14} /> Become a partner
                </a>
              </div>
            </div>
          </div>

          {/* WhatsApp channel + contact — secondary row inside the same panel. */}
          <div className="relative mt-10 pt-10 border-t border-[#26263A] grid lg:grid-cols-2 gap-6">
            <WhatsAppChannelCard variant="wide" />

            {/* Contact card */}
            <div className="bg-[#09090F]/70 border border-[#26263A] rounded-2xl p-6 backdrop-blur-sm">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#A78BFA]">
                Talk to us
              </div>
              <div className="mt-3 space-y-3 text-sm">
                <a
                  href="mailto:info@icaard.com"
                  className="flex items-center gap-3 hover:text-[#A78BFA] transition"
                  data-testid="contact-email"
                >
                  <span className="w-9 h-9 rounded-lg bg-[#7C3AED]/20 border border-[#7C3AED]/40 flex items-center justify-center text-[#A78BFA]">
                    <Mail size={16} />
                  </span>
                  <span className="font-medium">info@icaard.com</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-[#26263A] mt-16">
        <div className="max-w-6xl mx-auto px-6 pt-8 pb-2" data-testid="seo-about">
          <h2 className="font-display font-bold text-lg">About iCaard</h2>
          <p className="text-xs text-[#9CA3D4] mt-2 leading-relaxed max-w-3xl">
            iCaard brings everyday services together: food orders, events, transport tickets, gift cards,
            hostels, delivery, printing, professional services and smart queues. Customers can manage
            their passes, bookings, activity and profiles in one place. Dedicated portals help providers
            manage their services, while support agents help customers with approved, read only access.
            Made in Ghana, for Ghana.
          </p>
        </div>
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between text-[10px] text-[#9CA3D4]">
          <div>iCaard™ © 2026 · One Card, One System, Unlimited Access</div>
          <div className="flex flex-wrap gap-4">
            <a href="mailto:info@icaard.com" className="hover:text-white">info@icaard.com</a>
            <Link to="/legal" className="hover:text-white">Legal</Link>
          </div>
        </div>
      </footer>
      {selectedGiftPackage && <div className="fixed inset-0 z-[125] grid place-items-center bg-[#071B14]/75 p-4" role="dialog" aria-modal="true" aria-label="Buy gift card">
        <div className="icaard-gift-checkout-modal relative">
          <button type="button" aria-label="Close" onClick={() => setSelectedGiftPackage(null)} className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full bg-[#F1F7F2] text-[#37634C]"><X size={17}/></button>
          <div className="text-[9px] font-extrabold uppercase tracking-[.2em] text-[#147D60]">A gift from iCaard</div>
          <h2>Send {selectedGiftPackage.name}</h2>
          <p className="mt-2">Personalise your gift, pay securely with Paystack and share the gift link with its recipient.</p>
          <div className="mt-4 grid gap-3">
            <label>Recipient name<input autoComplete="name" maxLength={80} placeholder="Who is this gift for?" value={giftRecipient} onChange={e => setGiftRecipient(e.target.value)}/></label>
            <label>Occasion<select value={giftOccasion} onChange={e => setGiftOccasion(e.target.value)}><option value="general">Just because</option><option value="birthday">Birthday</option><option value="graduation">Graduation</option><option value="congratulations">Congratulations</option></select></label>
            <label>A personal message <span className="font-normal text-[#829188]">optional</span><textarea rows={2} maxLength={300} placeholder="Add a little note…" value={giftWish} onChange={e => setGiftWish(e.target.value)}/></label>
          </div>
          <div className="icaard-gift-checkout-total mt-4"><span>Gift {fmt(Number(selectedGiftPackage.price))}{giftServiceFeePct > 0 && <> + service fee ({giftServiceFeePct}%)</>}</span><strong>{fmt(Number(selectedGiftPackage.price) * (1 + giftServiceFeePct / 100))}</strong></div>
          {giftCheckoutError && <p className="icaard-gift-checkout-error mt-3" role="alert">{giftCheckoutError}</p>}
          <button type="button" className="icaard-gift-checkout-action mt-4" disabled={giftCheckoutBusy || !giftRecipient.trim()} onClick={beginGiftCheckout}>{giftCheckoutBusy ? <><Loader2 size={16} className="animate-spin"/>Opening secure checkout…</> : <>Continue to Paystack <ArrowRight size={16}/></>}</button>
          <p className="mb-0 mt-2 text-center text-[10px]">No account or email needed to buy. The recipient can sign in to claim the gift.</p>
        </div>
      </div>}
      {(guestGiftSuccess || giftReturnActive) && <div className="fixed inset-0 z-[130] grid place-items-center bg-[#071B14]/75 p-4" role="dialog" aria-modal="true" aria-label="Gift card payment status">
        <div className="icaard-gift-checkout-modal relative">
          {guestGiftSuccess ? <>
            <button type="button" aria-label="Close" onClick={() => { setGuestGiftSuccess(null); setGiftReturnActive(false); }} className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full bg-[#F1F7F2] text-[#37634C]"><X size={17}/></button>
            <span className="icaard-gift-success-mark"><Gift size={23}/></span>
            <h2 className="mt-4">Your gift is ready</h2>
            <p>Payment confirmed. Share the link below with {guestGiftSuccess.recipient_name || "your recipient"} so they can claim their {guestGiftSuccess.package_name} gift card.</p>
            <div className="my-4"><GiftCardVisual gift={guestGiftSuccess}/></div>
            <div className="icaard-gift-checkout-total mb-3"><span>Paid securely with Paystack</span><strong>{fmt(Number(guestGiftSuccess.amount || guestGiftSuccess.amount_paid || guestGiftSuccess.price))}</strong></div>
            <button type="button" className="icaard-gift-checkout-action" onClick={shareGuestGift}><Share2 size={16}/> Share gift link</button>
            <button type="button" className="mt-2 min-h-10 w-full rounded-xl border border-[#DCE8E0] bg-white text-sm font-bold text-[#456455]" onClick={() => { setGuestGiftSuccess(null); setGiftReturnActive(false); }}>Done</button>
          </> : <>
            <span className="icaard-gift-success-mark">{guestGiftVerifying ? <Loader2 size={22} className="animate-spin"/> : <Gift size={22}/>}</span>
            <h2 className="mt-4">{guestGiftVerifying ? "Checking your payment" : "Gift payment status"}</h2>
            <p>{giftCheckoutError || "Confirming your Paystack payment securely…"}</p>
            {!guestGiftVerifying && <button type="button" className="icaard-gift-checkout-action mt-4" onClick={verifyGuestGiftCheckout}>Check payment again <ArrowRight size={16}/></button>}
          </>}
        </div>
      </div>}
      {selectedEvent && <div className="fixed inset-0 z-[120] grid place-items-center bg-[#071B14]/70 p-4" role="dialog" aria-modal="true" aria-label="Buy event ticket">
        <div className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-3xl border border-[#DCE8E0] bg-white p-6 text-[#173B2D] shadow-2xl">
          <button type="button" aria-label="Close" onClick={() => setSelectedEvent(null)} className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full bg-[#F1F7F2] text-[#37634C]"><X size={18}/></button>
          <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#147D60]">Secure checkout</div>
          <h2 className="mt-2 pr-8 font-display text-2xl font-black">{selectedEvent.name}</h2>
          <p className="mt-2 text-sm text-[#607A6D]">Pay securely with Paystack. New to iCaard? Create an account with just your name and password, then your ticket will be saved in My Tickets.</p>
          {selectedEvent.age_restriction > 0 && <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl border border-[#F0C36A] bg-[#FFF8E9] p-3 text-sm text-[#624514]">
            <input type="checkbox" checked={guestAgeConfirmed} onChange={e => { setGuestAgeConfirmed(e.target.checked); setCheckoutError(""); }} className="mt-0.5 h-4 w-4 accent-[#147D60]" data-testid="guest-event-age-confirmation" />
            <span><strong>{selectedEvent.age_restriction}+ event.</strong> I confirm that I meet the age requirement. Your age may be checked at entry.</span>
          </label>}
          {(selectedEvent.ticket_tiers || []).length > 0 && <fieldset className="mt-5">
            <legend className="text-sm font-bold">Choose your ticket</legend>
            <div className="mt-2 grid max-h-64 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
              {(selectedEvent.ticket_tiers || []).map(t => {
                const soldOut = t.availability === "sold_out";
                const chosen = selectedTier === t.id;
                return <label key={t.id} className={`flex min-h-[64px] items-center justify-between gap-3 rounded-xl border p-3 transition ${soldOut ? "cursor-not-allowed border-[#E7EAE8] bg-[#F7F8F7] opacity-60" : chosen ? "cursor-pointer border-[#147D60] bg-[#EAF6EF] ring-2 ring-[#147D60]/15 focus-within:ring-2 focus-within:ring-[#F0B541]" : "cursor-pointer border-[#DCE8E0] bg-white hover:border-[#9ACDB0] hover:bg-[#F7FBF8] focus-within:ring-2 focus-within:ring-[#F0B541]"}`}>
                  <input type="radio" name="guest-event-ticket-tier" value={t.id} checked={chosen} disabled={soldOut} onChange={() => setSelectedTier(t.id)} className="sr-only" />
                  <span className="min-w-0">
                    <strong className="block truncate text-sm font-bold">{t.name}</strong>
                    {soldOut && <small className="mt-0.5 block text-[10px] font-semibold text-[#A23D28]">Sold out</small>}
                  </span>
                  <span className="shrink-0 text-sm font-extrabold text-[#147D60]">{fmt(Number(t.price))}</span>
                </label>;
              })}
            </div>
          </fieldset>}
          {checkoutError && <p role="alert" className="mt-3 rounded-xl bg-[#FFF1ED] p-3 text-sm text-[#A23D28]">{checkoutError}</p>}
          <button type="button" disabled={checkoutBusy || ((selectedEvent.ticket_tiers || []).length > 0 && !selectedTier) || (selectedEvent.age_restriction > 0 && !guestAgeConfirmed)} onClick={() => beginTicketCheckout(selectedEvent)} className="mt-5 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#147D60] px-4 text-sm font-bold text-white disabled:opacity-50">{checkoutBusy ? <><Loader2 size={17} className="animate-spin"/>Connecting securely…</> : user ? "Continue to Paystack" : "Create account & continue"}</button>
          <p className="mt-3 text-center text-[11px] text-[#71877A]">No email needed. After payment, your ticket is saved to My Tickets automatically.</p>
        </div>
      </div>}
    </div>
  );
}
